<template>
  <AppDialog
    :hide-hero="hideHero"
    :loading="loading"
    :show="show"
    :title="title"
    :type="type"
    :density="DIALOG_DENSITY.COMPACT"
    :disable-confirm-button
    :size="size"
    @on-confirm="onConfirm"
    @on-close="onClose"
    @after-enter="emitTransitionEvent('after-enter')"
    @before-enter="emitTransitionEvent('before-enter')"
    @after-leave="emitTransitionEvent('after-leave')"
  >
    <template v-for="(_, name) in appDialogSlots" :key="name" #[name]="slotProps">
      <slot :name="prependPrefix(APP_DIALOG_SLOT_NAME_PREFIX, name)" v-bind="slotProps || {}" />
    </template>

    <slot :name="prependPrefix(APP_DELETE_NAME_PREFIX, 'content')">
      <div class="adm-ModalBody">
        <slot :name="prependPrefix(APP_DELETE_NAME_PREFIX, 'message')">
          <i18n-t
            v-if="!isStringEmpty(keypath)"
            :keypath="keypath"
            scope="global"
            tag="div"
            v-bind="messageProps"
          >
            <template v-for="(_, name) in messageSlots" :key="name" #[name]="slotProps">
              <span class="adm-DangerText">
                <slot
                  :name="prependPrefix(I18N_T_SLOT_NAME_PREFIX, name)"
                  v-bind="slotProps || {}"
                />
              </span>
            </template>
          </i18n-t>
        </slot>

        <KeywordInput
          v-if="!isStringEmpty(keyword)"
          ref="keyword-input"
          :keyword="keyword"
          v-bind="keywordInputProps"
        />

        <!-- named slot `APP_DELETE_NAME_PREFIX${content-footer}` for additional footer content in the delete modal body -->
        <slot :name="prependPrefix(APP_DELETE_NAME_PREFIX, 'content-footer')" />
      </div>
    </slot>
  </AppDialog>
</template>

<script setup>
import { computed, useSlots, useTemplateRef } from 'vue'

import { DIALOG_DENSITY, DIALOG_TYPES } from '@/utils/components-configurations/app-dialog'
import { APP_MODAL_SIZES } from '@/utils/components-configurations/app-modal'
import { isStringEmpty } from '@/utils/general'
import { includesValue, modalSizePropValidator } from '@/utils/prop-validators'

import AppDialog from '@/components/AppDialog'
import KeywordInput from '@/components/ui/KeywordInput/KeywordInput'

defineOptions({
  name: 'AppDeleteModal'
})

const props = defineProps({
  // app dialog props
  show: {
    type: Boolean
  },

  loading: {
    type: Boolean
  },

  title: {
    type: String,
    default: ''
  },

  hideHero: {
    type: Boolean
  },

  disableConfirmButton: {
    type: Boolean
  },

  size: {
    type: [String, Number],
    default: APP_MODAL_SIZES.SM,
    validator: v => modalSizePropValidator(v)
  },

  type: {
    type: String,
    default: DIALOG_TYPES.DELETE,
    validator: v => includesValue(v, DIALOG_TYPES)
  },

  /**
   * The i18n-t keypath for the main message in the modal.
   */
  keypath: {
    type: String,
    default: ''
  },

  /**
   * @typedef {Record<string, unknown>} MessageProps
   *
   * You can pass any of the following props or any other custom props or attributes supported by vue.
   *
   * @property {string | object} [tag] - Used to wrap the content that is distributed in the slot. If omitted, the slot content is treated as Fragments.
   * @property {string} [locale] - Specifies the locale to be used for the component.
   * @property {string} [scope] - Specifies the scope to be used in the target component.
   * @property {string | number} [plural] - The Plural Choosing the message number prop
   * @property {string} [i18n] - A composer instance with an existing scope.
   * @property {string} [data-testid] - Test identifier
   * @property {string} [data-auto-testid] - Auto test identifier
   */

  messageProps: {
    /** @type {import('vue').PropType<MessageProps>} */
    type: Object,
    default: () => ({})
  },

  /**
   * The keyword required for KeywordInput (if any).
   */
  keyword: {
    type: String,
    default: ''
  },

  /**
   * @typedef {Record<string, unknown>} KeywordInputProps
   *
   * You can pass any of the following props or any other custom props or attributes supported by vue.
   *
   * @property {string} [data-testid] - Test identifier
   * @property {string} [data-auto-testid] - Auto test identifier
   */
  keywordInputProps: {
    /** @type {import('vue').PropType<KeywordInputProps>} */
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits({
  'on-close': null,
  'on-confirm': null,
  'after-enter': null,
  'before-enter': null,
  'after-leave': null
})

const slots = useSlots()

/**
 * 1. take all slots
 * 2. filter only those that start with the prefix. for example 'app-dialog:'
 * 3. collect them into an object, removing the prefix
 * 4. render slots from this object → add the prefix back → the slot list now contains only the slots we need for step 2
 *
 * @param {string} prefix - The slot name prefix to filter by (e.g., 'app-dialog:').
 * @returns {Object} An object mapping slot names (without prefix) to slot functions.
 */
const getSlotsByPrefix = prefix => {
  const slotKeys = Object.keys(slots).filter(slot => slot.startsWith(prefix))
  return slotKeys.reduce((acc, slot) => {
    acc[removePrefix(prefix, slot)] = slots[slot]
    return acc
  }, {})
}
const prependPrefix = (prefix, name) => `${prefix}${name}`
const removePrefix = (prefix, name) => name.replace(prefix, '')

const APP_DIALOG_SLOT_NAME_PREFIX = 'app-dialog:'
const I18N_T_SLOT_NAME_PREFIX = 'i18n-t:'
const APP_DELETE_NAME_PREFIX = 'app-delete-modal:'
const appDialogSlots = computed(() => getSlotsByPrefix(APP_DIALOG_SLOT_NAME_PREFIX))
const messageSlots = computed(() => getSlotsByPrefix(I18N_T_SLOT_NAME_PREFIX))

const keywordInputReference = useTemplateRef('keyword-input')

const onConfirm = () => {
  if (!props.keyword) {
    emit('on-confirm')
    return
  }

  if (!keywordInputReference.value) return

  const isKeywordValid = keywordInputReference.value.validate()

  if (isKeywordValid) {
    emit('on-confirm')
  }
}

const onClose = () => {
  emit('on-close')
}

const emitTransitionEvent = eventName => {
  emit(eventName)
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/modal-danger-text';

.adm-DangerText {
  @extend %modal-danger-text;
}

.adm-ModalBody {
  display: grid;
  gap: 20px;
}
</style>
